import { useCallback } from "react";
import { useRouter as useNextRouter } from "next/router";
import { UrlObject } from "url";

import { useCountry } from "./useCountry";
import { useLanguage } from "./useLanguage";
import { useIsLoading } from "../helpers/setIsLoadingContext";
import { buildLocalizedUrl } from "helpers";

type Url = string | UrlObject;

type NavigateOptions = {
  skipLocalePrefix?: boolean;
  shallow?: boolean;
  locale?: string | false;
  scroll?: boolean;
  withLoadingSplash?: boolean;
};

type PrefetchOptions = {
  skipLocalePrefix?: boolean;
  locale?: string;
};

// We have a weird bug that doubles query param: https://nf.com?id=123
// query.id would return ["123", "123"] instead of "123"
const normalizeQuery = <T extends Record<string, any>>(
  query: T
): Record<string, string | undefined> => {
  const normalized: Record<string, string | undefined> = {};

  for (const key in query) {
    const value = query[key];
    normalized[key] = Array.isArray(value) ? value[0] : value;
  }

  return normalized;
};

export function useRouter() {
  const router = useNextRouter();
  const country = useCountry();
  const language = useLanguage();
  const { setIsLoading } = useIsLoading();

  const getLocalizedUrls = useCallback(
    (url: Url, as?: Url, skipLocalePrefix?: boolean) => {
      const localizedUrl = buildLocalizedUrl(
        url,
        country,
        language,
        skipLocalePrefix
      );
      const localizedAs = as
        ? buildLocalizedUrl(as, country, language, skipLocalePrefix)
        : undefined;

      return { localizedUrl, localizedAs };
    },
    [country, language]
  );

  const push = useCallback(
    (url: Url, as?: Url, options: NavigateOptions = {}) => {
      const { skipLocalePrefix, withLoadingSplash, ...routerOptions } = options;

      if (withLoadingSplash) {
        setIsLoading(true);
      }

      const { localizedUrl, localizedAs } = getLocalizedUrls(
        url,
        as,
        skipLocalePrefix
      );

      return router
        .push(localizedUrl, localizedAs, routerOptions)
        .finally(() => {
          if (withLoadingSplash) setIsLoading(false);
        });
    },
    [router.push, getLocalizedUrls, setIsLoading]
  );

  const replace = useCallback(
    (url: Url, as?: Url, options: NavigateOptions = {}) => {
      const { skipLocalePrefix, withLoadingSplash, ...routerOptions } = options;

      if (withLoadingSplash) {
        setIsLoading(true);
      }

      const { localizedUrl, localizedAs } = getLocalizedUrls(
        url,
        as,
        skipLocalePrefix
      );

      return router
        .replace(localizedUrl, localizedAs, routerOptions)
        .then(() => {
          if (withLoadingSplash) setIsLoading(false);
        });
    },
    [router.replace, getLocalizedUrls, setIsLoading]
  );

  const prefetch = useCallback(
    (url: string, as?: string, options: PrefetchOptions = {}) => {
      const { skipLocalePrefix, ...prefetchOptions } = options;

      const { localizedUrl, localizedAs } = getLocalizedUrls(
        url,
        as,
        skipLocalePrefix
      );

      return router.prefetch(
        localizedUrl as string,
        localizedAs as string | undefined,
        prefetchOptions
      );
    },
    [router.prefetch, getLocalizedUrls]
  );

  return {
    ...router,
    query: normalizeQuery(router.query),
    push,
    replace,
    prefetch,
  };
}
